import { useEffect, useState } from "react";
import { Download, Mail, Archive, Sparkles, CreditCard, CheckCircle2, AlertCircle } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminGetConfig, adminUpdateConfig, adminRunGdpr, adminMspTestConnection, API } from "@/lib/api";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminConfig() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [archiveMonths, setArchiveMonths] = useState([]);
  const [cleaningAi, setCleaningAi] = useState(false);
  const [mspTest, setMspTest] = useState(null);
  const [mspTesting, setMspTesting] = useState(false);

  useEffect(() => {
    adminGetConfig().then(setCfg);
    api.get("/admin/bookkeeping/archive-months").then((r) => setArchiveMonths(r.data.months || [])).catch(() => {});
  }, []);

  function set(path, value) {
    setCfg((c) => {
      const n = { ...c };
      const keys = path.split(".");
      let cur = n;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return n;
    });
  }

  async function save() {
    await adminUpdateConfig(cfg);
    toast({ title: "Configuratie opgeslagen" });
  }

  async function runGdpr() {
    if (!window.confirm("GDPR retention nu uitvoeren?")) return;
    const res = await adminRunGdpr();
    toast({
      title: "GDPR cleanup uitgevoerd",
      description: `Soft-expired: ${res.soft_expired || 0} · Hard-deleted: ${res.hard_deleted || 0}`,
    });
  }

  async function testMsp() {
    setMspTesting(true);
    setMspTest(null);
    try {
      // First save any unsaved key changes
      await adminUpdateConfig(cfg);
      const res = await adminMspTestConnection();
      setMspTest(res);
      if (res.ok) {
        toast({ title: `MSP ${res.mode}: verbinding OK`, description: `${res.gateway_count} payment-methodes beschikbaar` });
      } else {
        toast({ title: `MSP ${res.mode}: faalt`, description: res.error, variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "MSP test mislukt", description: err.message, variant: "destructive" });
    } finally {
      setMspTesting(false);
    }
  }

  if (!cfg) return <AdminLayout><p>Laden…</p></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-config-page" className="space-y-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Configuratie</h1>
          <p className="text-ink-soft mt-1 text-sm">Globale instellingen voor CheckExpert.</p>
        </div>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink">Branding</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Merk-naam</Label>
              <Input value={cfg.brand?.name || ""} onChange={(e) => set("brand.name", e.target.value)} />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input value={cfg.brand?.tagline || ""} onChange={(e) => set("brand.tagline", e.target.value)} />
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-border p-6" data-testid="cfg-msp">
          <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-[#002FA7]" /> MultiSafepay — Betalingen
          </h2>
          <p className="text-sm text-ink-soft mt-1">Beheer je TEST en LIVE API keys hier. Schakel via &ldquo;Modus&rdquo; tussen test- en productieomgeving. Test je verbinding met de knop hieronder.</p>

          <div className="grid sm:grid-cols-3 gap-4 mt-5">
            <div>
              <Label>Modus</Label>
              <select
                value={cfg.msp_mode || "TEST"}
                onChange={(e) => set("msp_mode", e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="msp-mode-select"
              >
                <option value="TEST">TEST (sandbox)</option>
                <option value="LIVE">LIVE (productie)</option>
              </select>
              <p className="text-[11px] text-ink-soft mt-1">Actief: <span className={`font-semibold ${cfg.msp_mode === "LIVE" ? "text-red-600" : "text-emerald-600"}`}>{cfg.msp_mode || "TEST"}</span></p>
            </div>
            <div className="sm:col-span-2 flex items-end">
              <button
                onClick={testMsp}
                disabled={mspTesting}
                className="h-10 px-5 inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-60"
                data-testid="msp-test-btn"
              >
                {mspTesting ? "Testen…" : "Test verbinding"}
              </button>
              {mspTest && (
                <div className={`ml-3 text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1 ${mspTest.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="msp-test-result">
                  {mspTest.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                  {mspTest.ok ? `${mspTest.mode} OK — ${mspTest.gateway_count} methodes` : `${mspTest.mode}: ${mspTest.error}`}
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <Label className="flex items-center justify-between">
                <span>TEST API key</span>
                <span className="text-[10px] text-ink-soft font-normal">multisafepay.com → API → TEST</span>
              </Label>
              <Input
                type="password"
                value={cfg.msp_api_key_test || ""}
                onChange={(e) => set("msp_api_key_test", e.target.value)}
                placeholder="Plak hier je TEST key"
                data-testid="msp-key-test"
              />
            </div>
            <div>
              <Label className="flex items-center justify-between">
                <span>LIVE API key</span>
                <span className="text-[10px] text-red-600 font-semibold">⚠ alleen voor productie</span>
              </Label>
              <Input
                type="password"
                value={cfg.msp_api_key_live || ""}
                onChange={(e) => set("msp_api_key_live", e.target.value)}
                placeholder="Plak hier je LIVE key"
                data-testid="msp-key-live"
              />
            </div>
          </div>
          <p className="text-[11px] text-ink-soft mt-3">Tip: laat een veld leeg om te vallen op de waarde uit <code>backend/.env</code>. Klik &ldquo;Opslaan&rdquo; onderaan om wijzigingen door te voeren.</p>
        </section>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink">B2B abonnement</h2>
          <p className="text-sm text-ink-soft mt-1">Maandelijkse prijs voor bedrijfsabonnement (in eurocent). Single-check &amp; bundles beheer je hieronder via &ldquo;Credit Packs&rdquo;.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>B2B / maand (eurocent)</Label>
              <Input type="number" value={cfg.b2b_price_cents} onChange={(e) => set("b2b_price_cents", Number(e.target.value))} data-testid="cfg-b2b" />
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink">Tracking pixels</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Facebook Pixel ID</Label>
              <Input value={cfg.tracking?.facebook_pixel || ""} onChange={(e) => set("tracking.facebook_pixel", e.target.value)} />
            </div>
            <div>
              <Label>Google Analytics ID</Label>
              <Input value={cfg.tracking?.google_analytics || ""} onChange={(e) => set("tracking.google_analytics", e.target.value)} />
            </div>
            <div>
              <Label>TikTok Pixel</Label>
              <Input value={cfg.tracking?.tiktok_pixel || ""} onChange={(e) => set("tracking.tiktok_pixel", e.target.value)} />
            </div>
            <div>
              <Label>LinkedIn Insight</Label>
              <Input value={cfg.tracking?.linkedin_insight || ""} onChange={(e) => set("tracking.linkedin_insight", e.target.value)} />
            </div>
          </div>
        </section>

        {/* Credit Packs editor */}
        <section className="rounded-2xl bg-white border border-border p-6" data-testid="cfg-credit-packs">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-xl font-semibold text-ink">Credit Packs</h2>
              <p className="text-sm text-ink-soft mt-1">Beheer je credit-pakketten. Prijzen in eurocent, tijdsduur in maanden. Wijzigingen zijn direct live.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const newId = `pack-${Date.now()}`;
                const packs = [...(cfg.credit_packs || []), { id: newId, label: "Nieuw pack", credits: 1, price_cents: 499, validity_months: 12, visible: true, highlight: false, sort_order: 99 }];
                set("credit_packs", packs);
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-xs font-semibold"
              data-testid="add-credit-pack"
            >
              + Pack toevoegen
            </button>
          </div>
          <div className="mt-5 space-y-4">
            {(cfg.credit_packs || []).map((p, idx) => (
              <div key={p.id} className="rounded-xl border border-border bg-slate-50/50 p-4" data-testid={`cfg-pack-${p.id}`}>
                <div className="grid lg:grid-cols-[1fr_120px_120px_140px_100px_100px_60px] gap-3 items-end">
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input
                      value={p.label || ""}
                      onChange={(e) => {
                        const packs = [...(cfg.credit_packs || [])];
                        packs[idx] = { ...packs[idx], label: e.target.value };
                        set("credit_packs", packs);
                      }}
                      data-testid={`pack-label-${p.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Credits</Label>
                    <Input type="number" min="1" value={p.credits ?? 1} onChange={(e) => {
                      const packs = [...(cfg.credit_packs || [])];
                      packs[idx] = { ...packs[idx], credits: Number(e.target.value) };
                      set("credit_packs", packs);
                    }} />
                  </div>
                  <div>
                    <Label className="text-xs">Prijs (cent)</Label>
                    <Input type="number" min="0" value={p.price_cents ?? 0} onChange={(e) => {
                      const packs = [...(cfg.credit_packs || [])];
                      packs[idx] = { ...packs[idx], price_cents: Number(e.target.value) };
                      set("credit_packs", packs);
                    }} data-testid={`pack-price-${p.id}`} />
                    <p className="text-[10px] text-ink-subtle mt-1">€{((p.price_cents || 0) / 100).toFixed(2)}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Tijdsduur (maanden)</Label>
                    <Input type="number" min="1" max="60" value={p.validity_months ?? 12} onChange={(e) => {
                      const packs = [...(cfg.credit_packs || [])];
                      packs[idx] = { ...packs[idx], validity_months: Number(e.target.value) };
                      set("credit_packs", packs);
                    }} data-testid={`pack-validity-${p.id}`} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Zichtbaar</Label>
                    <input type="checkbox" checked={p.visible !== false} onChange={(e) => {
                      const packs = [...(cfg.credit_packs || [])];
                      packs[idx] = { ...packs[idx], visible: e.target.checked };
                      set("credit_packs", packs);
                    }} className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">Highlight</Label>
                    <input type="checkbox" checked={!!p.highlight} onChange={(e) => {
                      const packs = [...(cfg.credit_packs || [])];
                      packs[idx] = { ...packs[idx], highlight: e.target.checked };
                      set("credit_packs", packs);
                    }} className="h-5 w-5" />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm(`Pack "${p.label}" verwijderen?`)) return;
                      const packs = (cfg.credit_packs || []).filter((_, i) => i !== idx);
                      set("credit_packs", packs);
                    }}
                    className="text-red-600 hover:text-red-800 text-xs"
                    data-testid={`pack-delete-${p.id}`}
                  >
                    Verwijderen
                  </button>
                </div>
                <p className="text-[11px] text-ink-subtle mt-2">ID: <code>{p.id}</code></p>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-border flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Herinneringsmail X dagen voor verloop</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={cfg.expiry_reminder_days_before ?? 14}
                onChange={(e) => set("expiry_reminder_days_before", Number(e.target.value))}
                className="w-32"
                data-testid="cfg-expiry-days"
              />
            </div>
            <p className="text-xs text-ink-soft pb-2">Bewerk de mail-template via <code>email-templates → credits_expiring_soon</code>.</p>
          </div>
        </section>

        {/* reCAPTCHA v3 + Contact inbox */}
        <section className="rounded-2xl bg-white border border-border p-6" data-testid="cfg-recaptcha">
          <h2 className="font-display text-xl font-semibold text-ink">Contactformulier — anti-spam</h2>
          <p className="text-sm text-ink-soft mt-1">Bescherm je contactformulier met Google reCAPTCHA v3. Maak gratis een key aan op <a href="https://www.google.com/recaptcha/admin/create" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline">google.com/recaptcha</a> (kies &ldquo;v3&rdquo;). Laat de velden leeg om reCAPTCHA uit te schakelen.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-xs">Site key (publiek)</Label>
              <Input
                type="text"
                value={cfg.recaptcha?.site_key || ""}
                onChange={(e) => set("recaptcha", { ...(cfg.recaptcha || {}), site_key: e.target.value })}
                placeholder="6Lc..."
                data-testid="recaptcha-site-key"
              />
            </div>
            <div>
              <Label className="text-xs">Secret key (privé)</Label>
              <Input
                type="text"
                value={cfg.recaptcha?.secret_key || ""}
                onChange={(e) => set("recaptcha", { ...(cfg.recaptcha || {}), secret_key: e.target.value })}
                placeholder="6Lc..."
                data-testid="recaptcha-secret-key"
              />
            </div>
            <div>
              <Label className="text-xs">Minimum score (0.0-1.0)</Label>
              <Input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={cfg.recaptcha?.min_score ?? 0.5}
                onChange={(e) => set("recaptcha", { ...(cfg.recaptcha || {}), min_score: Number(e.target.value) })}
                data-testid="recaptcha-min-score"
              />
            </div>
            <div>
              <Label className="text-xs">Contact-inbox (waar bericht heen gaat)</Label>
              <Input
                type="email"
                value={cfg.contact_inbox || ""}
                onChange={(e) => set("contact_inbox", e.target.value)}
                placeholder="info@checkexpert.be"
                data-testid="contact-inbox"
              />
            </div>
          </div>
          <p className="text-[11px] text-ink-subtle mt-3">Tip: typisch 0,5 is een goed evenwicht. Hoger = strenger (meer false positives bij echte bezoekers).</p>
        </section>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink">Onderhoud &amp; GDPR</h2>
          <div className="flex items-center gap-3 mt-4">
            <input
              type="checkbox" id="maintenance"
              checked={!!cfg.maintenance_mode}
              onChange={(e) => set("maintenance_mode", e.target.checked)}
              data-testid="cfg-maintenance"
            />
            <Label htmlFor="maintenance">Maintenance mode (toont onderhoudspagina aan publiek)</Label>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <input
              type="checkbox" id="mock_mode"
              checked={!!cfg.mock_mode}
              onChange={(e) => set("mock_mode", e.target.checked)}
              data-testid="cfg-mock-mode"
            />
            <Label htmlFor="mock_mode">Mock mode (skip Anthropic calls, auto-blog, externe API&rsquo;s — handig voor smoke-testing)</Label>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <input
              type="checkbox" id="auto_blog_enabled"
              checked={cfg.auto_blog_enabled !== false}
              onChange={(e) => set("auto_blog_enabled", e.target.checked)}
              data-testid="cfg-auto-blog"
            />
            <Label htmlFor="auto_blog_enabled">Auto-blog actief (1 concept/dag om 9:30 Europe/Brussels)</Label>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <input
              type="checkbox" id="blog_enabled"
              checked={cfg.blog_enabled !== false}
              onChange={(e) => set("blog_enabled", e.target.checked)}
              data-testid="cfg-blog-enabled"
            />
            <Label htmlFor="blog_enabled">Blog tonen op site (uitvinken verbergt Blog in menu, footer en sitemap)</Label>
          </div>
          <button onClick={runGdpr} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold" data-testid="run-gdpr">
            Run GDPR cleanup nu
          </button>
        </section>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink">Boekhouding — Octopus-export</h2>
          <p className="text-sm text-ink-soft mt-1">Dagelijkse CSV-digest van alle orders (B2C check, bundles, B2B abonnementen). UTF-8 + BOM + ; -separator + komma decimaal, klaar voor Excel BE/NL.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Modus</Label>
              <select
                value={cfg.bookkeeping_mode || "off"}
                onChange={(e) => set("bookkeeping_mode", e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="cfg-bookkeeping-mode"
              >
                <option value="off">Uit</option>
                <option value="per_order">Per order (mail bij elke betaling)</option>
                <option value="daily_digest">Dagelijkse digest (06:00)</option>
              </select>
            </div>
            <div>
              <Label>Boekhouder e-mailadres</Label>
              <Input
                type="email"
                value={cfg.bookkeeping_email || ""}
                onChange={(e) => set("bookkeeping_email", e.target.value)}
                placeholder="boekhouding@webtron.be"
                data-testid="cfg-bookkeeping-email"
              />
            </div>
          </div>
          <div className="mt-5 flex gap-2 flex-wrap">
            <a
              href={`${API}/admin/bookkeeping/csv-yesterday`}
              onClick={(e) => {
                e.preventDefault();
                const token = localStorage.getItem("ce_admin_token");
                fetch(`${API}/admin/bookkeeping/csv-yesterday`, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `checkexpert-orders-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
              data-testid="download-csv"
            >
              <Download className="h-4 w-4" /> Download CSV (gisteren)
            </a>
            <button
              onClick={async () => {
                const r = await api.post("/admin/bookkeeping/send-now");
                toast({ title: r.data.sent ? "Digest verstuurd" : "Niet verstuurd", description: `Rows: ${r.data.rows || 0}` });
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold"
              data-testid="send-digest-now"
            >
              <Mail className="h-4 w-4" /> Verstuur digest nu
            </button>
          </div>

          {archiveMonths.length > 0 && (
            <div className="mt-6 pt-5 border-t border-border">
              <h3 className="font-display text-sm font-semibold text-ink flex items-center gap-2"><Archive className="h-4 w-4 text-[#002FA7]" /> Archief maandelijkse CSV&rsquo;s</h3>
              <p className="text-xs text-ink-soft mt-1">Download een CSV met alle orders per maand.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="csv-archive">
                {archiveMonths.map((m) => (
                  <button
                    key={m.year_month}
                    onClick={() => {
                      const token = localStorage.getItem("ce_admin_token");
                      fetch(`${API}/admin/bookkeeping/archive/${m.year_month}`, { headers: { Authorization: `Bearer ${token}` } })
                        .then((r) => r.blob())
                        .then((blob) => {
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `checkexpert-orders-${m.year_month}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                        });
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-ink text-xs font-semibold"
                    data-testid={`csv-archive-${m.year_month}`}
                  >
                    <Download className="h-3 w-3" /> {m.year_month} <span className="text-ink-soft">({m.count})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-2xl bg-white border border-border p-6" data-testid="cfg-octopus">
          <h2 className="font-display text-xl font-semibold text-ink">Octopus — kolommen & grootboekrekeningen</h2>
          <p className="text-sm text-ink-soft mt-1">Configureer welke kolommen in de CSV verschijnen + welke boekhoudcode (grootboekrekening) hoort bij elk type order. Wijzigingen zijn meteen actief.</p>

          {(() => {
            const oct = cfg.octopus_config || {};
            const cols = oct.columns || [];
            const defaultCodes = oct.default_codes || {};
            const packCodes = oct.pack_codes || {};
            const checkerCodes = oct.checker_codes || {};
            const packs = cfg.credit_packs || [];

            const moveCol = (i, dir) => {
              const next = [...cols];
              const j = i + dir;
              if (j < 0 || j >= next.length) return;
              [next[i], next[j]] = [next[j], next[i]];
              set("octopus_config.columns", next);
            };
            const toggleCol = (i) => {
              const next = cols.map((c, idx) => idx === i ? { ...c, enabled: !c.enabled } : c);
              set("octopus_config.columns", next);
            };
            const renameCol = (i, label) => {
              const next = cols.map((c, idx) => idx === i ? { ...c, label } : c);
              set("octopus_config.columns", next);
            };

            return (
              <div className="mt-5 space-y-6">
                {/* Columns */}
                <div>
                  <h3 className="font-display text-sm font-semibold text-ink mb-2">CSV-kolommen (volgorde, label, zichtbaar)</h3>
                  <div className="border border-border rounded-lg divide-y divide-border">
                    {cols.map((c, i) => (
                      <div key={c.key} className="flex items-center gap-2 px-3 py-2" data-testid={`oct-col-${c.key}`}>
                        <span className="text-xs text-ink-soft w-32 font-mono">{c.key}</span>
                        <Input value={c.label} onChange={(e) => renameCol(i, e.target.value)} className="h-8 text-sm flex-1" />
                        <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                          <input type="checkbox" checked={c.enabled !== false} onChange={() => toggleCol(i)} data-testid={`oct-col-toggle-${c.key}`} />
                          Actief
                        </label>
                        <button onClick={() => moveCol(i, -1)} className="text-ink-soft hover:text-ink px-1" aria-label="Omhoog">↑</button>
                        <button onClick={() => moveCol(i, 1)} className="text-ink-soft hover:text-ink px-1" aria-label="Omlaag">↓</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Default codes */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>B2C check (standaard)</Label>
                    <Input value={defaultCodes.b2c_check || ""} onChange={(e) => set("octopus_config.default_codes.b2c_check", e.target.value)} placeholder="700000" data-testid="oct-code-check" />
                  </div>
                  <div>
                    <Label>B2C bundle (standaard)</Label>
                    <Input value={defaultCodes.b2c_bundle || ""} onChange={(e) => set("octopus_config.default_codes.b2c_bundle", e.target.value)} placeholder="700100" data-testid="oct-code-bundle" />
                  </div>
                  <div>
                    <Label>B2B abonnement</Label>
                    <Input value={defaultCodes.b2b_subscription || ""} onChange={(e) => set("octopus_config.default_codes.b2b_subscription", e.target.value)} placeholder="705000" data-testid="oct-code-b2b" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Dagboek-code</Label>
                    <Input value={oct.journal_code || ""} onChange={(e) => set("octopus_config.journal_code", e.target.value)} placeholder="ALG" data-testid="oct-journal" />
                  </div>
                  <div>
                    <Label>Standaard BTW-tarief (%)</Label>
                    <Input type="number" min={0} max={100} value={oct.default_vat_rate ?? 21} onChange={(e) => set("octopus_config.default_vat_rate", Number(e.target.value))} data-testid="oct-vat" />
                  </div>
                </div>

                {/* Per-pack codes */}
                <div>
                  <h3 className="font-display text-sm font-semibold text-ink mb-2">Grootboekrekening per credit pack</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {packs.map((p) => (
                      <div key={p.id} className="flex items-center gap-3">
                        <span className="text-xs text-ink-soft w-32 truncate" title={p.label}>{p.id}</span>
                        <Input
                          value={packCodes[p.id] || ""}
                          onChange={(e) => set(`octopus_config.pack_codes.${p.id}`, e.target.value)}
                          placeholder={defaultCodes.b2c_bundle || "700100"}
                          data-testid={`oct-pack-code-${p.id}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Per-checker codes (free input) */}
                <div>
                  <h3 className="font-display text-sm font-semibold text-ink mb-2">Grootboekrekening per checker (optioneel)</h3>
                  <p className="text-xs text-ink-soft mb-2">Laat leeg om standaard B2C-code te gebruiken. Voeg per regel: <code>checker_slug: code</code></p>
                  <textarea
                    value={Object.entries(checkerCodes).map(([k, v]) => `${k}: ${v}`).join("\n")}
                    onChange={(e) => {
                      const map = {};
                      e.target.value.split("\n").forEach((line) => {
                        const m = line.match(/^\s*([\w-]+)\s*:\s*(\S+)\s*$/);
                        if (m) map[m[1]] = m[2];
                      });
                      set("octopus_config.checker_codes", map);
                    }}
                    rows={5}
                    className="w-full p-3 border border-input rounded-md text-xs font-mono"
                    placeholder={"huurcheck: 700000\npolischeck: 700010"}
                    data-testid="oct-checker-codes"
                  />
                </div>
              </div>
            );
          })()}
        </section>

        <section className="rounded-2xl bg-white border border-border p-6" data-testid="cfg-octopus-countries">
          <h2 className="font-display text-xl font-semibold text-ink">Octopus — Land × Klanttype → Boekingscode + BTW</h2>
          <p className="text-sm text-ink-soft mt-1">
            Volledig editeerbare tabel. Bij elke verkoop wordt het juiste boekingsaccountnummer + BTW-code gekozen op basis van het land van de klant en zijn type (bedrijf/particulier/overige). Gebruik <code>*</code> als land voor de wereldwijde catch-all.
          </p>

          {(() => {
            const rows = (cfg.octopus_config?.country_codes) || [];
            const update = (i, field, value) => {
              const next = rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r);
              set("octopus_config.country_codes", next);
            };
            const removeRow = (i) => set("octopus_config.country_codes", rows.filter((_, idx) => idx !== i));
            const addRow = () => set("octopus_config.country_codes", [...rows, { country: "BE", label: "", customer_type: "bedrijf", account: "", vat: "" }]);

            return (
              <div className="mt-5">
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary text-ink-soft uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="px-2 py-2 text-left">Land (ISO)</th>
                        <th className="px-2 py-2 text-left">Label</th>
                        <th className="px-2 py-2 text-left">Klanttype</th>
                        <th className="px-2 py-2 text-left">Boekingsaccount</th>
                        <th className="px-2 py-2 text-left">BTW-code</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="border-t border-border" data-testid={`oct-row-${i}`}>
                          <td className="px-2 py-1">
                            <input
                              value={r.country || ""}
                              onChange={(e) => update(i, "country", e.target.value.toUpperCase())}
                              className="w-16 px-2 py-1 border border-input rounded text-xs font-mono"
                              maxLength={3}
                              data-testid={`oct-row-${i}-country`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.label || ""}
                              onChange={(e) => update(i, "label", e.target.value)}
                              className="w-32 px-2 py-1 border border-input rounded text-xs"
                              data-testid={`oct-row-${i}-label`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <select
                              value={r.customer_type || "particulier"}
                              onChange={(e) => update(i, "customer_type", e.target.value)}
                              className="w-32 px-2 py-1 border border-input rounded text-xs bg-white"
                              data-testid={`oct-row-${i}-type`}
                            >
                              <option value="bedrijf">Bedrijf</option>
                              <option value="particulier">Particulier</option>
                              <option value="overige">Overige / Zonder factuur</option>
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.account || ""}
                              onChange={(e) => update(i, "account", e.target.value)}
                              className="w-24 px-2 py-1 border border-input rounded text-xs font-mono"
                              placeholder="700301"
                              data-testid={`oct-row-${i}-account`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={r.vat || ""}
                              onChange={(e) => update(i, "vat", e.target.value)}
                              className="w-24 px-2 py-1 border border-input rounded text-xs font-mono"
                              placeholder="Z1"
                              data-testid={`oct-row-${i}-vat`}
                            />
                          </td>
                          <td className="px-2 py-1 text-right">
                            <button
                              onClick={() => removeRow(i)}
                              className="text-red-600 hover:text-red-700 text-xs"
                              data-testid={`oct-row-${i}-delete`}
                              aria-label={`Verwijder rij ${i}`}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={addRow}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold"
                  data-testid="oct-row-add"
                >
                  + Voeg rij toe
                </button>
                <p className="text-[11px] text-ink-soft mt-2">
                  {rows.length} rijen actief. Bij geen exacte land-match wordt teruggevallen op land <code>*</code> (wereldwijd).
                </p>
              </div>
            );
          })()}
        </section>

        <section className="rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Content opschoning</h2>
          <p className="text-sm text-ink-soft mt-1">One-shot: vervang resterende &ldquo;AI / artificiële intelligentie&rdquo; vermeldingen in oude blog-posts door &ldquo;wij / expertise / doorlichting&rdquo;.</p>
          <button
            onClick={async () => {
              setCleaningAi(true);
              try {
                const r = await api.post("/admin/maintenance/clean-ai-mentions");
                toast({ title: `${r.data.cleaned} posts opgeschoond` });
              } finally {
                setCleaningAi(false);
              }
            }}
            disabled={cleaningAi}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="clean-ai-mentions"
          >
            {cleaningAi ? "Bezig…" : "Verwijder AI-vermeldingen uit blog"}
          </button>
        </section>

        <div className="sticky bottom-4 flex justify-end">
          <button onClick={save} className="px-7 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold shadow-lg" data-testid="save-config">
            Configuratie opslaan
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
